/**
 * Skeleton loading components for better perceived performance.
 *
 * Usage:
 *   <Skeleton className="w-32 h-4" />           -- basic rectangle
 *   <Skeleton.Text lines={3} />                  -- multi-line text block
 *   <Skeleton.Card />                            -- event card placeholder
 *   <Skeleton.EventDetail />                     -- event detail page placeholder
 */

function Skeleton({ className = 'w-full h-4' }) {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`animate-pulse bg-gray-200 rounded h-4 ${
            i === lines - 1 ? 'w-3/4' : 'w-full'
          }`}
        />
      ))}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="card-hover overflow-hidden" aria-hidden="true">
      {/* Image area */}
      <div className="h-44 bg-gray-200 animate-pulse" />

      {/* Content */}
      <div className="p-4">
        {/* Date badge + title */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 text-center">
            <div className="animate-pulse bg-gray-200 rounded w-8 h-3 mb-1" />
            <div className="animate-pulse bg-gray-200 rounded w-6 h-6 mx-auto" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="animate-pulse bg-gray-200 rounded h-5 w-3/4" />
            <div className="animate-pulse bg-gray-200 rounded h-4 w-1/3" />
          </div>
        </div>

        {/* Venue */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="animate-pulse bg-gray-200 rounded w-4 h-4 flex-shrink-0" />
          <div className="animate-pulse bg-gray-200 rounded h-4 w-2/3" />
        </div>

        {/* Price row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="animate-pulse bg-gray-200 rounded h-6 w-20" />
          <div className="animate-pulse bg-gray-200 rounded h-3 w-24" />
        </div>
      </div>
    </div>
  );
}

function SkeletonEventDetail() {
  return (
    <div className="max-w-4xl mx-auto" aria-hidden="true">
      {/* Hero Banner */}
      <div className="animate-pulse bg-gray-200 rounded-2xl h-72 mb-8" />

      <div className="grid md:grid-cols-3 gap-8">
        {/* Left column - Event details */}
        <div className="md:col-span-2">
          {/* Title */}
          <div className="animate-pulse bg-gray-200 rounded h-8 w-2/3 mb-4" />

          {/* Info cards */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="animate-pulse bg-gray-100 rounded-xl h-16 w-48" />
            <div className="animate-pulse bg-gray-100 rounded-xl h-16 w-40" />
          </div>

          {/* Organizer */}
          <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
            <div className="animate-pulse bg-gray-200 rounded-full w-12 h-12 flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="animate-pulse bg-gray-200 rounded h-3 w-20" />
              <div className="animate-pulse bg-gray-200 rounded h-4 w-32" />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2 mb-6">
            <div className="animate-pulse bg-gray-200 rounded h-5 w-40 mb-3" />
            <SkeletonText lines={4} />
          </div>

          {/* Trust signals */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex flex-wrap gap-6">
              <div className="animate-pulse bg-gray-200 rounded h-4 w-32" />
              <div className="animate-pulse bg-gray-200 rounded h-4 w-36" />
              <div className="animate-pulse bg-gray-200 rounded h-4 w-28" />
            </div>
          </div>
        </div>

        {/* Right column - Ticket panel */}
        <div className="card p-6 h-fit">
          <div className="animate-pulse bg-gray-200 rounded h-6 w-32 mb-4" />
          <div className="space-y-3 mb-6">
            <div className="animate-pulse bg-gray-100 rounded-lg h-16 w-full" />
            <div className="animate-pulse bg-gray-100 rounded-lg h-16 w-full" />
          </div>
          <div className="animate-pulse bg-gray-200 rounded-lg h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

Skeleton.Text = SkeletonText;
Skeleton.Card = SkeletonCard;
Skeleton.EventDetail = SkeletonEventDetail;

export default Skeleton;
